import React, { useState, useEffect, useMemo } from 'react'
import styles from '../CustomDomainModal.module.css'
import CTAButton from 'components/CTAButton/CTAButton'
import { ADD_DOMAIN } from '../constants/strings'
import { debounce } from 'lodash'
import CustomTooltip from '../../CustomTooltip'
import { Button } from 'react-bootstrap'
import DEEP_LINK from 'utils/deepLinks'
import { useHistory } from 'react-router-dom'
import { isMobileMode } from '../../ProPlans/PricingModalContent'
import {
  STATUS_CONNECTED,
  STATUS_INIT,
  STATUS_FAILED,
  STATUS_DISABLED,
  STATUS_INCOMPLETE
} from '../../../constants/customDomainStatus'

const AddDomainAddress = ({
  formik,
  onNext,
  setActiveStep,
  isServerFile = false,
  domains = [],
  onContinueSetup,
  onRetryDomain,
  onUploadToDomain,
  handleClose
}) => {
  const { errors, values, handleChange } = formik
  const [proceed, setProceed] = useState(false)
  const [error, setError] = useState(false)
  const [showWarningBox, setShowWarningBox] = useState(false)
  const [domainValue, setDomainValue] = useState('')
  const isErrorDomain = !!errors.dnsAddress?.domain
  const isErrorSubdomain = !!errors.dnsAddress?.subdomain
  const history = useHistory()

  const existingDomainRecord = useMemo(
    () => domains.find((d) => d.domain?.toLowerCase() === values.dnsAddress?.domain?.toLowerCase()),
    [domains, values.dnsAddress?.domain]
  )

  // Mirrors the status → action mapping used by the domains table (CustomDomainTable) so the
  // "domain already added" notice offers exactly the action that's valid for that record.
  const existingDomainAction = useMemo(() => {
    if (!existingDomainRecord) return null

    const status = (existingDomainRecord.status || STATUS_INCOMPLETE).toUpperCase()

    switch (status) {
      case STATUS_CONNECTED:
        return {
          message: 'This domain is already connected to your account.',
          actionLabel: 'Upload files to it',
          onAction: () => {
            handleClose?.()
            onUploadToDomain?.(existingDomainRecord)
          }
        }
      case STATUS_FAILED:
        return {
          message: 'You already added this domain, but the setup failed.',
          actionLabel: 'Retry setup',
          onAction: () => onRetryDomain?.(existingDomainRecord)
        }
      case STATUS_INIT:
        return {
          message: "You already added this domain and it's currently being validated. This can take up to 15 minutes.",
          actionLabel: null
        }
      case STATUS_DISABLED:
        return {
          message: 'This domain was disabled on your account. Please contact support to reconnect it.',
          actionLabel: null
        }
      case STATUS_INCOMPLETE:
      default:
        return {
          message: "You already added this domain, but haven't finished setting it up yet.",
          actionLabel: 'Continue setup',
          onAction: () => onContinueSetup?.(existingDomainRecord)
        }
    }
  }, [existingDomainRecord, handleClose, onContinueSetup, onRetryDomain, onUploadToDomain])

  useEffect(() => {
    const currentPath = window.location.pathname
    if (currentPath === DEEP_LINK.PURCHASE_CUSTOM_DOMAIN) {
      onPurchaseDomain()
    }
  }, [])

  const handleValidate = () => {
    if (isErrorDomain || isErrorSubdomain) {
      setError(true)
    } else {
      setError(false)
      setProceed(true)
    }
  }

  const ProceedFromSubDomain = () => {
    if (!isErrorDomain && !isErrorSubdomain) {
      onNext()
    } else {
      setError(true)
    }
  }

  const debouncedUpdateDomain = useMemo(
    () =>
      debounce((value) => {
        setDomainValue(value)
      }, 1200),
    []
  )

  const onPurchaseDomain = () => {
    history.replace({ pathname: '/manage/custom-domain/buy' })
    formik.setFieldValue(`dnsAddress.selected`, 'no')
    setActiveStep(2)
  }

  useEffect(() => {
    !isErrorDomain && !isErrorSubdomain && setError(false)
  }, [isErrorSubdomain, isErrorDomain])

  useEffect(() => {
    if (values.dnsAddress.checked) {
      setProceed(false)
    }
    !values.dnsAddress.checked && formik.setFieldValue('dnsAddress.subdomain', '')
  }, [values.dnsAddress.checked])

  useEffect(() => {
    debouncedUpdateDomain(values.dnsAddress.domain)

    return () => {
      debouncedUpdateDomain.cancel() // Cancel the debounced function
    }
  }, [values.dnsAddress.domain])

  useEffect(() => {
    if (values.dnsAddress.domain === domainValue) {
      const show = !values.dnsAddress.checked && domainValue && proceed
      setShowWarningBox(show)
    }
  }, [values.dnsAddress.checked, proceed, domainValue])

  return (
    <div className={styles.container}>
      <div className={styles.subHeading}>
        <div className={styles.title}>{ADD_DOMAIN.SUBHEADING}</div>
        <div className={styles.subTitle}>{ADD_DOMAIN.DESCRIPTION}</div>
      </div>
      <div className={styles.address}>
        <div className={styles.domainBox}>
          {values.dnsAddress.checked && (
            <>
              <input
                className={styles.addressInput}
                placeholder="subdomain"
                type="text"
                onChange={handleChange}
                name="dnsAddress.subdomain"
                value={values.dnsAddress.subdomain}
              />{' '}
              <span>.</span>
            </>
          )}
          <input
            className={styles.addressInput}
            placeholder="mywebsite.com"
            type="text"
            onChange={handleChange}
            name="dnsAddress.domain"
            value={values.dnsAddress?.domain}
          />
        </div>
        {(error || proceed) && errors?.dnsAddress?.domain && existingDomainAction ? (
          <span className={styles['validation-error']}>
            {existingDomainAction.message}
            {existingDomainAction.actionLabel && (
              <>
                {' '}
                <button type="button" className={styles.inlineLinkButton} onClick={existingDomainAction.onAction}>
                  {existingDomainAction.actionLabel}
                </button>
              </>
            )}
          </span>
        ) : (
          (error || proceed) &&
          errors?.dnsAddress?.domain && (
            <span className={styles['validation-error']}>
              {errors.dnsAddress.domain}
              <CustomTooltip label="Only letters, numbers & hyphens (-) are allowed. No spaces.">
                <span className="link ms-2">
                  <u>Help</u>
                </span>
              </CustomTooltip>
            </span>
          )
        )}
        {(error || proceed) && errors?.dnsAddress?.subdomain && (
          <span className={styles['validation-error']}>
            {errors.dnsAddress.subdomain}
            <CustomTooltip label="Only letters, numbers & hyphens (-) are allowed. No spaces.">
              <span className="link ms-2">
                <u>Help</u>
              </span>
            </CustomTooltip>
          </span>
        )}
        <div className={styles.checkbox}>
          <input
            type="checkbox"
            checked={values.dnsAddress.checked}
            onChange={() => formik.setFieldValue(`dnsAddress.checked`, !values.dnsAddress.checked)}
          />
          <span className={styles.subTitle}>
            I want to use a subdomain{' '}
            <span className="font-small">
              (e.g. xyz.mydomain.com{' '}
              <a
                href="https://helpdesk.tiiny.host/en/article/connecting-your-custom-domain-using-a-subdomain-nnmug2/"
                target="_blank"
                rel="noreferrer"
              >
                learn more
              </a>
              )
            </span>
          </span>
        </div>
      </div>
      {showWarningBox && (
        <div className={styles.warningBox}>
          <div className={styles.warningHeading}>
            <img src="/assets/icons/warning-yellow.svg" alt="warning" width="24" height="24" />
            <span>Warning</span>{' '}
          </div>
          <div className={styles.text}>
            <p>
              Any existing website hosted on <span>{domainValue}</span> will{' '}
              <u>
                <b>no longer be accessible</b>
              </u>{' '}
              if you continue.
            </p>
            <p>
              If you want to host your files on an address like <b>myfile.{domainValue}</b>, please select{' '}
              <b>Subdomain</b>.
            </p>
          </div>
        </div>
      )}
      {!isMobileMode() && !isServerFile && (
        <div className="mt-3">
          <Button variant="outline-primary" size="sm" onClick={onPurchaseDomain}>
            Or purchase a domain
          </Button>
        </div>
      )}
      <div className={styles.btnContainer} style={{ justifyContent: 'flex-end' }}>
        {values.dnsAddress.checked ? (
          <>
            <Button onClick={ProceedFromSubDomain} size="small" className={styles.forwardButton} disabled={error}>
              Continue
            </Button>
          </>
        ) : (
          <>
            {!error && proceed ? (
              <Button onClick={onNext} size="small" className={styles.forwardButton} disabled={isErrorDomain || error}>
                Continue
              </Button>
            ) : (
              <Button onClick={handleValidate} size="small" className={styles.forwardButton}>
                Next
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default AddDomainAddress
